import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// ─── DATE HELPERS ───────────────────────────────────────

// Format: 24th May 2026
function formatDateWithSuffix(dateInput) {
  const d = new Date(dateInput);

  const day = d.getDate();
  const month = d.toLocaleString('en-IN', { month: 'long' });
  const year = d.getFullYear();

  const getSuffix = (day) => {
    if (day >= 11 && day <= 13) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  return `${day}${getSuffix(day)} ${month} ${year}`;
}

function todayFormatted() {
  return formatDateWithSuffix(new Date());
}

// ─── HEADER ─────────────────────────────────────────────

function getHeader() {
  return `
    <div style="
      background:#002668;
      display:flex;
      align-items:center;
      width:100%;
    ">
      <img
        src="/Mensa_letterhead.jpg"
        style="width:800px;"
        alt="logo"
      />
    </div>
  `;
}

// ─── SUCCESS TEMPLATE ───────────────────────────────────

function buildSuccessfulHTML({ name, letterDate, testDate, testLocation, score }) {
  return `
    ${getHeader()}

    <div style="
      padding:30px 54px 40px 54px;
      font-family:'Times New Roman';
      font-size:14pt;
      line-height:1.7;
      letter-spacing:0.2px;
      color:#000;
      text-align: justify;
    ">

      <table style="width:100%; margin-bottom:18px;">
        <tr>
          <td style="width:60%;">
            To,<br>
            <strong>${name}</strong>
          </td>
          <td style="text-align:right;">
            Date: ${letterDate}
          </td>
        </tr>
      </table>

      <p>Dear ${name},</p>

      <p>
        This is in regards to the Supervised Mensa Test held on <strong>${testDate}</strong>
        in <strong>${testLocation}</strong>. Your score places you in the
        <strong>${score}</strong> percentile. This implies that you are amongst
        the most intelligent people in our country.
      </p>

      <p>
        In view of your excellent score, I am delighted to extend an invitation to you
        to join <strong>Mensa India</strong>, the Indian chapter of Mensa International.
      </p>

      <p>
        You can use this letter as proof of having qualified for Mensa India Membership
        at any time of your life. It is your responsibility to keep this safe.
      </p>

      <p>
        Apart from the diverse activities of your home chapter, as a member of Mensa India,
        almost all National Mensa Chapters worldwide are likely to extend you the courtesy
        of treating you as a guest member and will invite you to join their activities when
        you are in their country.
      </p>

      <p>
        I do hope you will take this opportunity to be part of the world&#39;s largest and
        oldest high IQ society. On behalf of Mensa India and on my personal behalf, I look
        forward to welcoming you to our Society.
      </p>
      <div style="margin-top:25px;">
      <p>With best wishes,</p>
      <p style="margin:0;">For Mensa India</p>

      <div style="margin-top:40px;">
        <div style="height: 100px;"></div>
        <p style="margin:0">Dr. Meenakshi Gokhale</p>
        <p style="margin:0;">National Supervisory Psychologist</p>
      </div>

    </div>
  `;
}

// ─── FAIL TEMPLATE ──────────────────────────────────────

function buildUnsuccessfulHTML({ name, letterDate, testDate, testLocation, score }) {

  // Score Fix
  let formattedScore = score;

  if (!isNaN(score)) {
    const numericScore = Number(score);

    if (numericScore < 25) {
      formattedScore = "Below 25";
    } else {
      formattedScore = `${numericScore}`;
    }
  }

  return `
    ${getHeader()}

    <div style="
      padding:30px 54px 40px 54px;
      font-family:'Times New Roman';
      font-size:14pt;
      line-height:1.7;
      color:#000;
      letter-spacing: 0.2px;
      text-align: justify;
    ">

      <table style="width:100%; margin-bottom:18px;">
        <tr>
          <td style="width:60%;">
            To,<br>
            <strong>${name}</strong>
          </td>
          <td style="text-align:right;">
            Date: ${letterDate}
          </td>
        </tr>
      </table>

      <p>Dear ${name},</p>

      <p>
        This is with regards to the Supervised Mensa Test held on <strong>${testDate}</strong>
        in <strong>${testLocation}</strong>. Your result in this test places you in the
        <strong>${formattedScore}</strong> percentile.
      </p>

      <p>
        As you are perhaps aware, the minimum qualification for joining Mensa is the
        98th percentile, in view of this, I regret we are unable to offer you Mensa
        membership at this stage. However, many factors can affect your test results
        and you are welcome to re-appear for the Mensa test after a gap of one year.
        If you choose to take the test again, I extend you my best wishes.
      </p>

      <p>
        I would like to mention that our IQ test measures a narrow range of intelligence,
        which is generally defined as &#34;General Intelligence&#34;. Apart from this, one
        can possess many different kinds of intelligences. Our Mensa test is not apt to
        measure these. You may not have qualified for Mensa Membership but you may still
        be brilliant in so many other fields such as music, arts, sports, dance, writing
        and other forms of giftedness. You may like to explore, and focus on, activities
        that you have passion for.
      </p>
      <div style="margin-top:25px;">
      <p>With best wishes,</p>
      <p style="margin:0;">For Mensa India</p>
      
      <div style="margin-top:40px;">
        <div style="height:100px;"></div>
        <p style="margin:0;">Dr. Meenakshi Gokhale</p>
        <p style="margin:0;">National Supervisory Psychologist</p>
      </div>

    </div>
  `;
}

// ─── MAIN FUNCTION ──────────────────────────────────────

export async function generateCertificate({
  candidate,
  testDate,
  testLocation,
  letterDate,
}) {

  const isPass =
    String(candidate.result_status ?? '').toLowerCase() === 'pass';

  const name = `${candidate.first_name ?? ''} ${candidate.last_name ?? ''}`.trim();

  // Apply date formatting everywhere
  const formattedLetterDate = letterDate
    ? formatDateWithSuffix(letterDate)
    : todayFormatted();

  const formattedTestDate = formatDateWithSuffix(testDate);

  const score = candidate.marks_obtain ?? '—';

  const htmlContent = isPass
    ? buildSuccessfulHTML({
        name,
        letterDate: formattedLetterDate,
        testDate: formattedTestDate,
        testLocation,
        score
      })
    : buildUnsuccessfulHTML({
        name,
        letterDate: formattedLetterDate,
        testDate: formattedTestDate,
        testLocation,
        score
      });

  const wrapper = document.createElement('div');
  wrapper.style.cssText = `
    position:fixed;
    top:-9999px;
    left:-9999px;
    width:794px;
    background:#fff;
  `;

  wrapper.innerHTML = htmlContent;
  document.body.appendChild(wrapper);

  try {
    const canvas = await html2canvas(wrapper, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
    });

    const imgData = canvas.toDataURL("image/jpeg", 0.95);

    const pdf = new jsPDF("p", "mm", "a4");

    const pdfWidth = 210;
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, pdfHeight);

    pdf.save(`${name.replace(/\s+/g, "_")}.pdf`);

  } finally {
    document.body.removeChild(wrapper);
  }
}